import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckoutResponseSchema,
  LedgerResponseSchema,
  PlansResponseSchema,
  PortalResponseSchema,
  PurchasesResponseSchema,
  SubscriptionResponseSchema,
  UsageResponseSchema,
  WalletsResponseSchema,
  type LedgerEntryType,
  type Resource,
} from '@growhero/shared';
import { api, newIdempotencyKey } from '@/lib/api';
import { tenantKey, useApiQuery } from '@/lib/query';

/** Public plan catalog (no tenant needed — also used before the first payment). */
export function usePlans() {
  return useQuery({
    queryKey: ['billing-plans'],
    queryFn: ({ signal }) =>
      api.get('/billing/plans', { schema: PlansResponseSchema, noTenant: true, signal }),
    staleTime: 5 * 60_000,
  });
}

export const useSubscription = () =>
  useApiQuery(['billing', 'subscription'], '/billing/subscription', {
    schema: SubscriptionResponseSchema,
  });

export const useWallets = (refetchInterval?: number | false) =>
  useApiQuery(['billing', 'wallets'], '/billing/wallets', {
    schema: WalletsResponseSchema,
    refetchInterval,
  });

export const useUsage = (days: number) =>
  useApiQuery(['billing', 'usage', days], '/billing/usage', {
    schema: UsageResponseSchema,
    query: { days },
  });

export const useLedger = (q: {
  resource?: Resource;
  entryType?: LedgerEntryType;
  cursor?: string;
}) =>
  useApiQuery(['billing', 'ledger', q], '/billing/ledger', {
    schema: LedgerResponseSchema,
    query: { limit: 15, ...q },
  });

export const usePurchases = () =>
  useApiQuery(['billing', 'purchases'], '/billing/purchases', { schema: PurchasesResponseSchema });

/** Stable across renders (safe in effect dependencies). */
export function useInvalidateBilling() {
  const qc = useQueryClient();
  return useCallback(
    () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: tenantKey('billing') }),
        qc.invalidateQueries({ queryKey: ['tenant-current'] }),
      ]),
    [qc],
  );
}

export const startCheckout = (planKey: string) =>
  api.post(
    '/billing/checkout',
    { planKey },
    { schema: CheckoutResponseSchema, idempotencyKey: newIdempotencyKey() },
  );

export const startTopup = (bundleKey: string) =>
  api.post(
    '/billing/topups/checkout',
    { bundleKey },
    { schema: CheckoutResponseSchema, idempotencyKey: newIdempotencyKey() },
  );

export const openPortal = () => api.post('/billing/portal', {}, { schema: PortalResponseSchema });

/**
 * Leave for a checkout/portal URL. Fake-mode URLs point at this app: navigate in-app with the
 * path only, so the flow works whatever host/port the dev server runs on.
 */
export function goToCheckout(url: string, navigate: (to: string) => void) {
  try {
    const u = new URL(url, window.location.origin);
    if (u.pathname.startsWith('/billing') || u.pathname.startsWith('/setup')) {
      navigate(`${u.pathname}${u.search}`);
      return;
    }
  } catch {
    /* fall through */
  }
  window.location.assign(url);
}
